import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSameActionMutation } from '@/hooks/useSingleFlightMutation';
import { AxiosError } from 'axios';
import { toast } from 'sonner';
import {
  getOnboardingProgress,
  completeStep,
  skipStep,
  goToStep,
  completeOnboarding,
} from '@/services/onboarding';

const ONBOARDING_KEY = 'onboarding';

export function useOnboardingProgress(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [ONBOARDING_KEY],
    queryFn: getOnboardingProgress,
    staleTime: 30000, // 30 seconds
    enabled: options?.enabled ?? true,
  });
}

export function useCompleteStep() {
  const queryClient = useQueryClient();

  return useSameActionMutation({
    mutationFn: (stepKey: string) => completeStep(stepKey),
    onSuccess: (data) => {
      queryClient.setQueryData([ONBOARDING_KEY], data);
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      const message = error.response?.data?.message || 'Erro ao completar passo';
      toast.error(message);
    },
  });
}

export function useSkipStep() {
  const queryClient = useQueryClient();

  return useSameActionMutation({
    mutationFn: (stepKey: string) => skipStep(stepKey),
    onSuccess: (data) => {
      queryClient.setQueryData([ONBOARDING_KEY], data);
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      const message = error.response?.data?.message || 'Erro ao pular passo';
      toast.error(message);
    },
  });
}

export function useGoToStep() {
  const queryClient = useQueryClient();

  return useSameActionMutation({
    mutationFn: (step: number) => goToStep(step),
    onSuccess: (data) => {
      queryClient.setQueryData([ONBOARDING_KEY], data);
    },
  });
}

export function useCompleteOnboarding() {
  const queryClient = useQueryClient();

  return useSameActionMutation({
    mutationFn: () => completeOnboarding(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ONBOARDING_KEY] });
      toast.success('Configuração concluída! Bem-vinda ao ValiosasCRM!');
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      const message = error.response?.data?.message || 'Erro ao finalizar configuração';
      toast.error(message);
    },
  });
}
